/** @file Server frames → stores: who is online, commits applied in order or fetched, resync, errors. */
import { docStore } from '../../stores/docStore';
import { liveStore } from '../../stores/liveStore';
import type { ServerFrame } from '../live/serverFrame';
import { applyCommit } from './applyCommit';
import { hashCheck } from './hashCheck';
import { kickSync } from './kickSync';
import type { SyncSession } from './liveSession';
import { mergeByKey } from './mergeByKey';
import { pushNotice } from './pushNotice';

type Handlers = { [K in ServerFrame['t']]: (session: SyncSession, frame: Extract<ServerFrame, { t: K }>) => void };

/** The render feed keeps the newest 200 entries (the server keeps the same). */
const MAX_RENDERS = 200;

const revOf = (): number => docStore.getState().slice?.rev ?? 0;
const raise = (session: SyncSession, rev: number): void => {
  session.knownRev = Math.max(session.knownRev, rev);
};
/** The loop has work: a reload is still wanted (an earlier GET /doc failed) or `rev` is past the confirmed rev. */
const needsSync = (session: SyncSession, rev: number): boolean => session.resyncWanted || rev > revOf();
const withoutActor = (actors: Record<string, string | null>, actor: string): Record<string, string | null> =>
  Object.fromEntries(Object.entries(actors).filter(([name]) => name !== actor));

/** The next commit with patches is applied at once unless a reload is wanted; anything else raises knownRev and
 *  runs the loop (E8-R1). */
function onCommit(session: SyncSession, frame: Extract<ServerFrame, { t: 'commit' }>): void {
  raise(session, frame.rev);
  if (session.busy === null && !session.resyncWanted && frame.patches !== null && frame.rev === revOf() + 1) {
    applyCommit(session, frame.summary, frame.patches);
    hashCheck.flush(session);
    return;
  }
  if (needsSync(session, frame.rev)) void kickSync(session);
}

const HANDLERS: Handlers = {
  hello: (session, frame) => {
    liveStore.setState({ actors: Object.fromEntries(frame.actors.map((a) => [a.name, a.editing])) });
    raise(session, frame.rev);
    session.resyncWanted ||= frame.rev < revOf();
    session.hashDue = true;
    void kickSync(session);
  },
  commit: onCommit,
  presence: (_session, frame) => { liveStore.setState((s) => ({ actors: { ...s.actors, [frame.actor]: frame.editing } })); },
  leave: (_session, frame) => { liveStore.setState((s) => ({ actors: withoutActor(s.actors, frame.actor) })); },
  pong: (session, frame) => {
    raise(session, frame.rev);
    if (needsSync(session, frame.rev)) void kickSync(session);
  },
  resync: (session) => {
    session.resyncWanted = true;
    void kickSync(session);
  },
  render: (_session, frame) => { liveStore.setState((s) => ({ renders: mergeByKey(s.renders, [frame.entry], (e) => e.seq, MAX_RENDERS) })); },
  renders: (_session, frame) => {
    liveStore.setState((s) => ({ renders: mergeByKey(s.renders, frame.entries, (e) => e.seq, MAX_RENDERS), rendersMore: frame.more }));
  },
  error: (_session, frame) => { pushNotice(null, frame.error); },
};

/** Routes one parsed frame to its handler (exhaustive over `ServerFrame['t']`). */
export function handleFrame(session: SyncSession, frame: ServerFrame): void {
  (HANDLERS[frame.t] as (s: SyncSession, f: ServerFrame) => void)(session, frame);
}
